/**
 * 前端 API 客户端。
 *
 * 封装 fetch，自动注入 Clerk session token。
 * 统一错误处理（401 跳 Clerk 登录弹窗；非 2xx 抛 ApiError，携带响应 body）。
 *
 * Clerk 通过 ClerkProvider 加载，运行时 window.Clerk 可用。
 */

import { trackEvent } from "./analytics";

/** window.Clerk 的最小类型（Clerk JS SDK 加载后挂载） */
interface ClerkGlobal {
  session?: {
    getToken(): Promise<string | null>;
  };
  openSignIn?: () => void;
}

function getClerk(): ClerkGlobal | null {
  if (typeof window === "undefined") return null;
  return (window as unknown as { Clerk?: ClerkGlobal }).Clerk ?? null;
}

/** API 错误 */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** 获取当前 Clerk session token（未登录时返回 null） */
export async function getToken(): Promise<string | null> {
  try {
    const clerk = getClerk();
    const token = await clerk?.session?.getToken();
    return token ?? null;
  } catch {
    return null;
  }
}

/** 触发 Clerk 登录弹窗 */
function openSignIn(): void {
  getClerk()?.openSignIn?.();
}

export interface RequestOptions extends RequestInit {
  /** 是否解析为 JSON（默认 true） */
  json?: boolean;
}

/** 发起 API 请求，自动注入 Bearer token */
export async function apiFetch<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { json = true, headers: extraHeaders, ...rest } = options;

  const token = await getToken();
  const headers: Record<string, string> = {
    ...(extraHeaders as Record<string, string>),
  };
  if (token && !headers["Authorization"]) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(path, { ...rest, headers });

  if (res.status === 401) {
    // 触发 Clerk 登录
    openSignIn();
    throw new ApiError(401, "unauthorized");
  }

  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = undefined;
    }
    throw new ApiError(res.status, `API error ${res.status}`, body);
  }

  return (json ? await res.json() : res) as T;
}

// ── 类型定义（与后端 API 对齐）──

/** GET /api/config 返回：客户端运行时环境与可用模型 */
export interface AppConfig {
  environment: string;
  /** 可选模型列表（全环境返回；生成页模型选择列表数据源） */
  models: string[];
  defaultModel: string;
  /** M5: 站点配置（KV 存储 + 默认值兜底） */
  siteConfig: SiteConfig;
}

export interface CreateGenerationResponse {
  generationId: string;
  status: string;
  providerTaskId: string;
}

export interface GenerationStatus {
  id: string;
  status: "queued" | "running" | "succeeded" | "failed" | "expired";
  progress: number;
  estimatedSecondsRemaining: number;
  glbReady: boolean;
  glbUrl: string | null;
  glbSizeBytes: number | null;
  stlReady: boolean;
  threemfReady: boolean;
  provider: string;
  errorMessage: string | null;
  /** 水密 QA 结果（passed/failed/error；QA 未跑为 null；明细仅存服务端 D1） */
  qaStatus?: "passed" | "failed" | "error" | null;
  createdAt: number;
  updatedAt: number;
}

// ── 具体 API 调用 ──

/** 上传图片（multipart），返回 uploadId */
export async function uploadImage(
  file: File,
): Promise<{
  uploadId: string;
  r2Key: string;
  mimeType: string;
  width: number | null;
  height: number | null;
}> {
  const token = await getToken();
  const form = new FormData();
  form.append("image", file);

  const res = await fetch("/api/uploads", {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });

  if (res.status === 401) {
    openSignIn();
    throw new ApiError(401, "unauthorized");
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.error ?? "upload failed", body);
  }
  return res.json();
}

/** 创建生成任务（成功受理即记 generation_start） */
export async function createGeneration(params: {
  uploadId: string;
  subdivLevel?: "medium" | "high";
  model?: string;
}): Promise<CreateGenerationResponse> {
  const res = await apiFetch<CreateGenerationResponse>("/api/generations", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  trackEvent("generation_start", {
    subdiv_level: params.subdivLevel ?? "medium",
  });
  return res;
}

// ── 文生图（Workers AI / FLUX.2 klein）──

/** 文生图响应：返回 uploadId（已存 R2 + uploads 表，可直接用于图生 3D） */
export interface GenerateImageResponse {
  uploadId: string;
  mimeType: string;
  width: number;
  height: number;
  /** 私有缩略图 URL（GET /api/uploads/:id/thumbnail，带 Clerk token 才能访问） */
  thumbnailUrl: string;
  /** prompt 是否经后端翻译为英语后生成（多语言适配；uploads 表存原文） */
  promptTranslated?: boolean;
}

/** 文生图（同步返回）。ratio + resolution 组合决定最终尺寸（后端换算）。
 * prompt 语言不限（站点四语 UI 均可），非英语由后端统一翻译为英语后生成 */
export async function generateImage(params: {
  prompt: string;
  /** 模型 id（Workers AI model id），缺省由后端决定 */
  model?: string;
  /** 宽高比，如 "1:1" / "16:9"（与后端 RATIO_RESOLUTION_TABLE 对齐） */
  ratio?: string;
  /** 分辨率档位 "1K" | "2K"，缺省 "2K" */
  resolution?: string;
}): Promise<GenerateImageResponse> {
  const res = await apiFetch<GenerateImageResponse>("/api/image-generations", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  trackEvent("image_generation_start", {
    resolution: params.resolution ?? "2K",
  });
  return res;
}

/**
 * 提示词优化（Workers AI，免费）：把用户描述改写为更适合
 * 「文生图 → 图生 3D」流水线的提示词，成功后回填输入框。
 */
export async function optimizePrompt(params: {
  prompt: string;
}): Promise<{ prompt: string }> {
  return apiFetch<{ prompt: string }>(
    "/api/image-generations/optimize-prompt",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(params),
    },
  );
}

/** 文生图作品列表项（「我的作品」图片 tab） */
export interface ImageWorkItem {
  id: string;
  prompt: string | null;
  /** 私有缩略图 URL（GET /api/uploads/:id/thumbnail，带 Clerk token 才能访问） */
  thumbnailUrl: string;
  width: number | null;
  height: number | null;
  createdAt: number;
}

/** 分页查询文生图作品 */
export async function listImageWorks(params?: {
  cursor?: number;
  limit?: number;
}): Promise<{ items: ImageWorkItem[]; nextCursor: number | null }> {
  const qs = new URLSearchParams();
  if (params?.cursor) qs.set("cursor", String(params.cursor));
  if (params?.limit) qs.set("limit", String(params.limit));
  const query = qs.toString();
  return apiFetch(`/api/image-generations${query ? `?${query}` : ""}`);
}

/** 查询生成任务状态 */
export async function getGeneration(
  id: string,
): Promise<GenerationStatus> {
  return apiFetch<GenerationStatus>(`/api/generations/${id}`);
}

/** 查询客户端运行时配置（环境 + 可用模型） */
export async function getConfig(): Promise<AppConfig> {
  return apiFetch<AppConfig>("/api/config");
}

// ── 站点配置（M5，合并进 GET /api/config）──

/** 站点配置（与后端 services/site-config.ts 对齐） */
export interface SiteConfig {
  brandName: string;
  tagline: string;
  seoDefaultTitle: string;
  seoDefaultDescription: string;
  contactEmail: string;
  social: { twitter?: string; github?: string; discord?: string };
  footerLinks: { label: string; href: string }[];
}

/** GET /api/pricing 返回：从 Creem 实时读取的套餐价格（带后端 KV 缓存） */
export interface PricingResponse {
  /** 各付费档价格（free 不含，永远 $0）；null 表示后端未取到（前端用硬编码兜底） */
  tiers: Record<
    "starter" | "pro" | "studio",
    { month: number | null; year: number | null }
  >;
  currency: string;
  fetchedAt: number;
}

/** 拉取实时套餐价格 */
export async function getPricing(): Promise<PricingResponse> {
  return apiFetch<PricingResponse>("/api/pricing");
}

/** 重试失败任务 */
export async function retryGeneration(
  id: string,
): Promise<CreateGenerationResponse> {
  return apiFetch<CreateGenerationResponse>(`/api/generations/${id}/retry`, {
    method: "POST",
  });
}

/** 生成历史列表项（轻量，用于侧边栏） */
export interface GenerationListItem {
  id: string;
  status: string;
  provider: string;
  glbReady: boolean;
  /** 「我的作品」原图缩略图 URL（GET /api/generations/:id/image） */
  imageUrl?: string | null;
  /** 水密 QA 结果（passed/failed/error；QA 未跑为 null） */
  qaStatus?: "passed" | "failed" | "error" | null;
  createdAt: number;
  updatedAt: number;
}

/** 分页查询生成历史 */
export async function listGenerations(params?: {
  cursor?: number;
  limit?: number;
}): Promise<{ items: GenerationListItem[]; nextCursor: number | null }> {
  const qs = new URLSearchParams();
  if (params?.cursor) qs.set("cursor", String(params.cursor));
  if (params?.limit) qs.set("limit", String(params.limit));
  const query = qs.toString();
  return apiFetch(`/api/generations${query ? `?${query}` : ""}`);
}

// ── 积分系统（M2）──

export interface CreditBalance {
  signup_bonus: number;
  free_monthly: number;
  subscription: number;
  addon: number;
  plaza_reward: number;
  total: number;
}

export interface CreditBucketDetail {
  type: "signup_bonus" | "free_monthly" | "subscription" | "addon" | "plaza_reward";
  remaining: number;
  initial: number;
  periodKey: string | null;
  expiresAt: number | null;
}

export interface CreditsResponse extends CreditBalance {
  buckets: CreditBucketDetail[];
}

export interface UsageResponse {
  thisMonth: {
    generated: number;
    succeeded: number;
    failed: number;
    consumed?: number;
    /** 分组：3D 模型用量（0010 起；旧后端可能不返回，使用方需兜底） */
    models?: { generated: number; succeeded: number; failed: number; consumed?: number };
    /** 分组：文生图用量（0010 起；旧后端可能不返回） */
    images?: { generated: number; consumed?: number };
  };
  total: {
    generated: number;
    succeeded: number;
    consumed?: number;
    models?: { generated: number; succeeded: number; consumed?: number };
    images?: { generated: number; consumed?: number };
  };
}

/** 查询当前用户积分余额 + 明细 */
export async function getCredits(): Promise<CreditsResponse> {
  return apiFetch<CreditsResponse>("/api/credits");
}

/** 查询当前用户用量统计 */
export async function getUsage(): Promise<UsageResponse> {
  return apiFetch<UsageResponse>("/api/usage");
}

/**
 * 从文章封面 R2 key（`media/<uuid>.<ext>`）提取 media id 拼公开 URL。
 * 无法解析时返回 null（前台降级不显示封面）。Blog 系列页面共用（P3 去重）。
 */
export function coverMediaUrl(r2Key: string | null): string | null {
  if (!r2Key) return null;
  const m = r2Key.match(/^media\/([a-f0-9-]+)\.\w+$/i);
  return m ? `/api/media/${m[1]}` : null;
}

// ── 计费系统（M3）──

export type Tier = "starter" | "pro" | "studio";
export type BillingInterval = "month" | "year";

/** 创建 Checkout Session（订阅 / 加油包）。成功创建即记 checkout_click（用户点了购买） */
export async function createCheckout(params: {
  type?: "subscription" | "addon";
  tier?: Tier;
  interval?: BillingInterval;
}): Promise<{ url: string }> {
  const res = await apiFetch<{ url: string }>("/api/billing/checkout", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  trackEvent("checkout_click", {
    type: params.type ?? "subscription",
    tier: params.tier ?? "",
    interval: params.interval ?? "",
  });
  return res;
}

/** 升降级订阅 */
export async function changeSubscription(
  tier: Tier,
  interval: BillingInterval,
): Promise<{ prorated: boolean; effectiveAt: "now" | "period_end" }> {
  return apiFetch("/api/billing/change", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tier, interval }),
  });
}

/** 创建 Creem Customer Portal 链接（取消/换卡） */
export async function createPortalSession(): Promise<{ url: string }> {
  return apiFetch<{ url: string }>("/api/billing/portal-session", {
    method: "POST",
  });
}

/** 授权类型 */
export type LicenseType =
  | "personal"
  | "basic_commercial"
  | "full_commercial";

/** 当前授权信息 */
export interface LicenseInfo {
  tier: string;
  /** 活跃订阅的计费周期（无活跃订阅为 null；定价页切换月/年判断用） */
  subscriptionInterval: "month" | "year" | null;
  /** 活跃订阅当前周期结束时间（ms；升级「下周期生效」提醒展示用） */
  subscriptionPeriodEnd: number | null;
  licenseType: LicenseType | null;
  licenseTitle: string | null;
  licenseTerms: string | null;
  certificate: { id: string; issuedAt: number; expiresAt: number | null } | null;
}

/** 查当前授权 */
export async function getLicense(): Promise<LicenseInfo> {
  return apiFetch<LicenseInfo>("/api/license");
}

// ── 广场系统（M4）──

/**
 * 广场下载积分成本：与 worker 的 DOWNLOAD_COST（services/plaza.ts）保持一致，
 * 仅用于前端展示扣积分提示（WorkPreviewModal 的 downloadCost）。
 * 首页 Showcase 与广场页共用，保证两处弹窗提示一致。
 */
export const PLAZA_DOWNLOAD_COST = 25;

export type ModelCategory =
  | "characters"
  | "animals"
  | "household"
  | "toys"
  | "art"
  | "tools"
  | "other";

/** 广场卡片摘要 */
export interface PlazaModelSummary {
  id: string;
  title: string;
  category: ModelCategory | null;
  tags?: string | null;
  licenseType: LicenseType | null;
  likes: number;
  downloads: number;
  views: number;
  userId: string;
  authorName: string;
  publishedAt: number;
  sourceImageR2Key?: string | null;
  isSeed: boolean;
}

/** 广场列表响应 */
export interface PlazaListResponse {
  items: PlazaModelSummary[];
  /**
   * 下一页游标。字段含义取决于 sort：
   * - newest：publishedAt + id
   * - popular：likes + id
   * 前端原样回传对应字段即可（listPlaza 内部按需带上）。
   */
  nextCursor: {
    publishedAt: number | null;
    likes: number | null;
    id: string;
  } | null;
}

/** 浏览广场 */
export async function listPlaza(params?: {
  category?: ModelCategory;
  sort?: "newest" | "popular";
  cursorCreatedAt?: number;
  cursorLikes?: number;
  cursorId?: string;
  limit?: number;
}): Promise<PlazaListResponse> {
  const qs = new URLSearchParams();
  if (params?.category) qs.set("category", params.category);
  if (params?.sort) qs.set("sort", params.sort);
  if (params?.cursorCreatedAt) qs.set("cursorCreatedAt", String(params.cursorCreatedAt));
  if (params?.cursorLikes !== undefined && params?.cursorLikes !== null)
    qs.set("cursorLikes", String(params.cursorLikes));
  if (params?.cursorId) qs.set("cursorId", params.cursorId);
  if (params?.limit) qs.set("limit", String(params.limit));
  const query = qs.toString();
  // 落地页预取：prerender 注入 preload link 预热请求，lib/plaza-prefetch.ts
  // 在 bundle 求值期消费并写信封（比组件挂载早）。按 query 精确匹配一次性
  // 消费，失败/未命中回落常规请求。
  const prefetched = takePlazaListPrefetch(query);
  if (prefetched) {
    const data = (await prefetched) as PlazaListResponse | null;
    if (data) return data;
  }
  return apiFetch(`/api/plaza${query ? `?${query}` : ""}`);
}

/** 预渲染页预取的广场数据缓存（window 上的一次性信封），见 listPlaza。 */
type PlazaListPrefetch = { query: string; promise: Promise<unknown> };
function takePlazaListPrefetch(query: string): Promise<unknown> | null {
  const w = window as unknown as {
    __TSUVI_PLAZA_LIST_PREFETCH__?: PlazaListPrefetch | null;
  };
  const p = w.__TSUVI_PLAZA_LIST_PREFETCH__;
  if (p && p.query === query) {
    w.__TSUVI_PLAZA_LIST_PREFETCH__ = null;
    return p.promise;
  }
  return null;
}

/** 搜索广场 */
export async function searchPlaza(
  q: string,
  category?: ModelCategory,
): Promise<PlazaListResponse> {
  const qs = new URLSearchParams({ q });
  if (category) qs.set("category", category);
  return apiFetch(`/api/plaza/search?${qs.toString()}`);
}

/** 创建草稿 */
export async function createDraft(
  generationId: string,
): Promise<{ modelId: string }> {
  return apiFetch("/api/plaza/draft", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ generationId }),
  });
}

/** 更新草稿 */
export async function updateDraft(
  modelId: string,
  data: {
    title: string;
    category: ModelCategory;
    tags: string[];
    description?: string;
  },
): Promise<void> {
  await apiFetch(`/api/plaza/${modelId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

/** 提交发布 */
export async function submitModel(
  modelId: string,
): Promise<{ status: "published" | "pending"; rewardGranted: boolean }> {
  return apiFetch(`/api/plaza/${modelId}/submit`, { method: "POST" });
}

/** 我的收藏列表 */
export async function listMyFavorites(params?: {
  cursorCreatedAt?: number;
  cursorId?: string;
  limit?: number;
}): Promise<PlazaListResponse> {
  const qs = new URLSearchParams();
  if (params?.cursorCreatedAt) qs.set("cursorCreatedAt", String(params.cursorCreatedAt));
  if (params?.cursorId) qs.set("cursorId", params.cursorId);
  if (params?.limit) qs.set("limit", String(params.limit));
  const query = qs.toString();
  return apiFetch(`/api/plaza/me/favorites${query ? `?${query}` : ""}`);
}

// ── 博客前台（公开）──

/** 公开文章摘要（列表用） */
export interface BlogPostSummary {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  coverR2Key: string | null;
  tags: string[];
  publishedAt: number;
  readingMinutes: number;
}

/** 公开文章详情 */
export interface BlogPostDetail extends BlogPostSummary {
  bodyMd: string;
  /** 内容最后修订时间（Article JSON-LD dateModified 用） */
  updatedAt: number;
}

/** 公开博客列表（q=关键词 / tag=标签 服务端过滤） */
export async function listBlogPosts(params?: {
  limit?: number;
  offset?: number;
  q?: string;
  tag?: string;
}): Promise<{ items: BlogPostSummary[]; total: number }> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.offset) qs.set("offset", String(params.offset));
  if (params?.q) qs.set("q", params.q);
  if (params?.tag) qs.set("tag", params.tag);
  const query = qs.toString();
  return apiFetch(`/api/blog${query ? `?${query}` : ""}`);
}

/** 标签聚合项（博客快速过滤 chip 用） */
export interface BlogTagCount {
  tag: string;
  count: number;
}

/** 已发布文章的标签聚合（按热度倒序） */
export async function listBlogTags(): Promise<BlogTagCount[]> {
  const res = await apiFetch<{ tags: BlogTagCount[] }>("/api/blog/tags");
  return res.tags;
}

/** 公开博客详情 */
export async function getBlogPost(slug: string): Promise<BlogPostDetail> {
  return apiFetch<BlogPostDetail>(`/api/blog/${slug}`);
}
